import React, { useEffect, useRef } from "react";
import { Helmet } from "react-helmet";
import { useOutletContext, useLocation } from "react-router";
import { useIntl } from "react-intl";
import { useAuth, useProfile, useProfileAutoFill } from "hooks";
import { MEMBER_STATUS, MISSION_ROLE_STATES, AGENT_TASK_STATE, BANNER_TYPE } from "constants/index";
import { ProfileBox, AVAILABILITY_COMPONENTS } from "components/Profile";
import { Grid } from "components/Containers";
import Card from "components/Card";
import { CandidateTitle } from "components/Candidate";
import { Banner } from "components/Banner";
import { margins } from "style";

/**
 * Profile Page
 */
const Profile = () => {
  const { hash } = useLocation();
  const preferredRateRef = useRef(null);

  const { profileId, canViewEditRate, canViewEditFullTimeEmployment } = useAuth();
  const { application } = useOutletContext();
  const intl = useIntl();
  const { data, loading } = useProfile({ profileId });
  const { task } = useProfileAutoFill(profileId);
  const profileStatus = data?.profile?.status;
  const isProcessingResume = task?.state === AGENT_TASK_STATE.processing;
  const canEditProfile = profileStatus !== MEMBER_STATUS.reviewing && !isProcessingResume;
  const keyName = "profile";
  const key = `Availability${canViewEditRate ? "Rate" : ""}${canViewEditFullTimeEmployment ? "Employment" : ""}`;
  const Component = AVAILABILITY_COMPONENTS[key];
  const disabled = application?.missionRole?.state === MISSION_ROLE_STATES.closed;

  useEffect(() => {
    if (hash === "#preferred-rate" && !loading) {
      preferredRateRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [hash, loading]);

  return (
    <>
      <Helmet>
        <title>{intl.formatMessage({ id: "Meta.Candidate.Profile.Title" })}</title>
      </Helmet>
      <Grid>
        <Grid.col start={1} end={13}>
          <CandidateTitle title="Candidate.Profile.Title" description="Candidate.Profile.Body" profileId={profileId} />
        </Grid.col>
        {isProcessingResume && (
          <Grid.col start={1} end={13}>
            <Banner isClosable={false} type={BANNER_TYPE.warning} marginSize={margins.none}>
              {intl.formatMessage({ id: "Candidate.Profile.ResumeProcessing" })}
            </Banner>
          </Grid.col>
        )}
        <Grid.col start={1} end={13}>
          <ProfileBox id={profileId} isEditable={canEditProfile && !disabled} />
        </Grid.col>
        <Grid.col start={1} end={13} id="preferred-rate" ref={preferredRateRef}>
          <Card>
            <Component data={data && data[keyName]} loading={loading} isEditable={canEditProfile && !disabled} />
          </Card>
        </Grid.col>
      </Grid>
    </>
  );
};

export default Profile;
